/**
 * Request a Quote modal — the automated replacement for "call or email us for a quote".
 * Works for both products (garment + print method + quantity) and services (Screen Printing /
 * Digital Printing) since pricing here always depends on design/quantity, never a fixed
 * checkout price. See docs/modules.md "Quote requests" in the platform monorepo.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { usePortal } from '../context/PortalContext';
import OptionCard from './OptionCard';
import SuccessState from './SuccessState';
import { submitProductQuoteRequest, submitServiceQuoteRequest, uploadDesignArtwork } from '../services/api';
import { ACCEPTED_ARTWORK_ACCEPT_ATTR, ACCEPTED_ARTWORK_LABEL, validateArtworkFile } from '../config/artwork';

const PRINT_METHODS = [
  { value: 'screen_printing', label: 'Screen Printing (bulk/repeat runs, min ~10-20 items)' },
  { value: 'digital_printing', label: 'Digital Printing / DTF (no minimum, one-off ok)' },
];

export default function RequestQuoteModal({ target, onClose }) {
  // target: { type: 'product' | 'service', id, name }
  const { branding, portal } = usePortal();
  const businessId = portal?.business?.id;
  const [quantity, setQuantity] = useState(target?.type === 'product' ? '20' : '');
  const [printMethod, setPrintMethod] = useState('screen_printing');
  const [garmentSource, setGarmentSource] = useState('printer_supplies');
  const [artworkUrl, setArtworkUrl] = useState('');
  const [uploadedArtworkUrl, setUploadedArtworkUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!target) return null;

  const buildJobDescription = () => {
    const parts = [];
    if (target.type === 'product') {
      parts.push(`${quantity || '?'}x ${target.name}`);
      parts.push(printMethod === 'screen_printing' ? 'Screen printing' : 'Digital printing (DTF)');
      parts.push(garmentSource === 'customer_supplies' ? 'customer will supply garment' : 'use printer stock');
    } else {
      parts.push(`${target.name} enquiry`);
    }
    if (notes.trim()) parts.push(notes.trim());
    return parts.join(' — ');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateArtworkFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }
    if (!businessId) {
      setUploadError('Could not resolve this store — please try again shortly.');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const url = await uploadDesignArtwork(file, businessId);
      setUploadedArtworkUrl(url);
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const finalArtworkUrl = uploadedArtworkUrl || artworkUrl.trim();
      const payload = {
        jobDescription: buildJobDescription(),
        guestName: name.trim(),
        guestEmail: email.trim(),
        guestPhone: phone.trim() || undefined,
        photos: finalArtworkUrl ? [finalArtworkUrl] : undefined,
        urgency: 'normal',
        designData:
          target.type === 'product'
            ? {
                garmentSource,
                quantity: parseInt(quantity, 10) || undefined,
                printMethod,
                // Only the uploaded (our-own-R2) URL gets a synthetic placement entry — a pasted
                // Drive/Dropbox link isn't something the backend can safely fetch to quality-check
                // (see artwork-quality.service.ts's isTrustedArtworkUrl).
                placements: uploadedArtworkUrl ? [{ placement: 'primary', artworkUrl: uploadedArtworkUrl }] : undefined,
              }
            : undefined,
      };
      if (target.type === 'product') {
        await submitProductQuoteRequest(target.id, payload);
      } else {
        await submitServiceQuoteRequest(target.id, payload);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again or call/email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} style={styles.closeBtn} aria-label="Close">
          <X size={20} />
        </button>

        {submitted ? (
          <SuccessState
            title="Quote request sent"
            message={`We've got your request for ${target.name}. We'll email you a price once we've reviewed it.`}
            steps={['Submitted', 'We review', 'You get a price']}
          >
            <button type="button" onClick={onClose} className="btn btn-primary" style={{ marginTop: '8px' }}>
              Close
            </button>
          </SuccessState>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={{ marginBottom: '4px' }}>Request a Quote</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>{target.name}</p>

            {error && <div style={styles.error}>{error}</div>}

            {target.type === 'product' && (
              <>
                <label style={{ ...styles.label, marginBottom: '14px' }}>
                  Quantity
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    style={styles.input}
                    required
                  />
                </label>

                <div style={{ ...styles.label, marginBottom: '10px' }}>Print method</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                  {PRINT_METHODS.map((m) => (
                    <OptionCard
                      key={m.value}
                      title={m.label.split(' (')[0]}
                      description={m.label.includes('(') ? `(${m.label.split(' (')[1]}` : undefined}
                      selected={printMethod === m.value}
                      onClick={() => setPrintMethod(m.value)}
                    />
                  ))}
                </div>

                <div style={{ ...styles.label, marginBottom: '10px' }}>Garment</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                  <OptionCard
                    title="Use your stock"
                    description="This product"
                    selected={garmentSource === 'printer_supplies'}
                    onClick={() => setGarmentSource('printer_supplies')}
                  />
                  <OptionCard
                    title="I'll supply my own"
                    selected={garmentSource === 'customer_supplies'}
                    onClick={() => setGarmentSource('customer_supplies')}
                  />
                </div>
              </>
            )}

            <label style={{ ...styles.label, marginBottom: uploadedArtworkUrl ? '4px' : '14px' }}>
              Upload your artwork (optional — {ACCEPTED_ARTWORK_LABEL})
              <input
                type="file"
                accept={ACCEPTED_ARTWORK_ACCEPT_ATTR}
                disabled={uploading}
                onChange={handleFileChange}
                style={{ fontSize: '13px' }}
              />
            </label>
            {uploading && <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>Uploading…</div>}
            {uploadError && <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '10px' }}>{uploadError}</div>}
            {uploadedArtworkUrl && (
              <div style={{ fontSize: '12px', color: 'var(--primary-color)', marginBottom: '14px' }}>✓ Artwork uploaded</div>
            )}

            {!uploadedArtworkUrl && (
              <label style={{ ...styles.label, marginBottom: '14px' }}>
                …or paste a link instead (Drive/Dropbox, or leave blank and we'll follow up by email)
                <input
                  type="url"
                  value={artworkUrl}
                  onChange={(e) => setArtworkUrl(e.target.value)}
                  placeholder="https://..."
                  style={styles.input}
                />
              </label>
            )}

            <label style={{ ...styles.label, marginBottom: '14px' }}>
              Notes
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                style={{ ...styles.input, resize: 'vertical' }}
                placeholder="Colours, placement, sizes needed, deadline..."
              />
            </label>

            <div style={styles.row}>
              <label style={styles.label}>
                Your name
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={styles.input} required />
              </label>
              <label style={styles.label}>
                Email
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={styles.input} required />
              </label>
            </div>
            <label style={{ ...styles.label, marginBottom: '20px' }}>
              Phone (optional)
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} style={styles.input} />
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary"
              style={{ width: '100%', background: branding.primaryColor, border: 'none' }}
            >
              {submitting ? 'Sending…' : 'Send Quote Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    padding: 'clamp(20px, 5vw, 28px)',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '14px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#444',
    flex: '1 1 160px',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'inherit',
  },
  error: {
    background: '#fef2f2',
    color: '#ef4444',
    padding: '10px 14px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
  },
};
